import React, { useState, useEffect } from 'react';
import costVariationsService from '../services/costVariationsService';
import LoadingSpinner from './LoadingSpinner';
import ConfirmModal from './ConfirmModal.jsx';

const VARIATION_TYPES = {
  'color': 'Cor',
  'thickness': 'Espessura',
  'size': 'Tamanho',
  'brand': 'Marca',
  'model': 'Modelo'
};

const CostVariationsManager = ({ costId, costData, onVariationsChange }) => {
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingVariation, setEditingVariation] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Formulário
  const [formData, setFormData] = useState({
    variation_type: 'color',
    variation_value: '',
    variation_unit: '',
    price_modifier: 0,
    price_multiplier: 1
  });

  // Dados para cálculo
  const [calculationData, setCalculationData] = useState({
    quantity: 1,
    color: '',
    thickness: '',
    size_width: '',
    size_height: '',
    size_length: '',
    brand: '',
    model: ''
  });

  useEffect(() => {
    if (costId) {
      loadVariations();
    }
  }, [costId]);

  const loadVariations = async () => {
    try {
      setLoading(true);
      const response = await costVariationsService.getCostVariations(costId);
      setVariations(response.data);
    } catch (error) {
      setError('Erro ao carregar variações');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCalculationChange = (e) => {
    const { name, value } = e.target;
    setCalculationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVariation) {
        await costVariationsService.updateVariation(editingVariation.id, formData);
      } else {
        await costVariationsService.createVariation(costId, formData);
      }

      setShowForm(false);
      setEditingVariation(null);
      resetForm();
      loadVariations();
      if (onVariationsChange) onVariationsChange();
    } catch (error) {
      setError('Erro ao salvar variação');
      console.error(error);
    }
  };

  const handleEdit = (variation) => {
    setEditingVariation(variation);
    setFormData({
      variation_type: variation.variation_type,
      variation_value: variation.variation_value,
      variation_unit: variation.variation_unit || '',
      price_modifier: variation.price_modifier,
      price_multiplier: variation.price_multiplier
    });
    setShowForm(true);
  };

  const handleDelete = (variationId) => {
    setPendingDeleteId(variationId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId) {
      try {
        await costVariationsService.deleteVariation(pendingDeleteId);
        loadVariations();
        if (onVariationsChange) onVariationsChange();
      } catch (error) {
        setError('Erro ao remover variação');
        console.error(error);
      }
    }
    setShowConfirmModal(false);
    setPendingDeleteId(null);
  };

  const handleCalculatePrice = async () => {
    try {
      const response = await costVariationsService.calculatePrice(costId, calculationData);
      setCalculationResult(response.data);
    } catch (error) {
      setError('Erro ao calcular preço');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      variation_type: 'color',
      variation_value: '',
      variation_unit: '',
      price_modifier: 0,
      price_multiplier: 1
    });
  };

  const handleNewVariation = () => {
    setEditingVariation(null);
    resetForm();
    setShowForm(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Calculadora de Preços */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">🧮 Calculadora de Preços</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantidade
            </label>
            <input
              type="number"
              name="quantity"
              value={calculationData.quantity}
              onChange={handleCalculationChange}
              min="1"
              step="0.1"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cor
            </label>
            <input
              type="text"
              name="color"
              value={calculationData.color}
              onChange={handleCalculationChange}
              placeholder="Ex: Branco, Nogal"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Espessura (mm)
            </label>
            <input
              type="number"
              name="thickness"
              value={calculationData.thickness}
              onChange={handleCalculationChange}
              step="0.1"
              placeholder="Ex: 15, 18"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Largura (cm)
            </label>
            <input
              type="number"
              name="size_width"
              value={calculationData.size_width}
              onChange={handleCalculationChange}
              step="0.1"
              placeholder="Ex: 275"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Altura (cm)
            </label>
            <input
              type="number"
              name="size_height"
              value={calculationData.size_height}
              onChange={handleCalculationChange}
              step="0.1"
              placeholder="Ex: 185"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Marca
            </label>
            <input
              type="text"
              name="brand"
              value={calculationData.brand}
              onChange={handleCalculationChange}
              placeholder="Ex: Palowa, Leo Madeiras"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <button
          onClick={handleCalculatePrice}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          🧮 Calcular Preço
        </button>
      </div>

      {/* Resultado do Cálculo */}
      {calculationResult && (
        <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-green-800 mb-4">💰 Resultado do Cálculo</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="font-medium text-green-700 mb-2">Detalhamento:</h5>
              <ul className="space-y-1 text-sm text-green-600">
                <li>Preço base: {formatCurrency(calculationResult.calculation_breakdown.base_price)}</li>
                <li>Após modificadores: {formatCurrency(calculationResult.calculation_breakdown.after_modifiers)}</li>
                <li>Após multiplicadores: {formatCurrency(calculationResult.calculation_breakdown.after_multipliers)}</li>
                <li>Após quantidade: {formatCurrency(calculationResult.calculation_breakdown.after_quantity)}</li>
                <li>Com margem ({calculationResult.margin_percentage}%): {formatCurrency(calculationResult.calculation_breakdown.with_margin)}</li>
              </ul>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-800">
                {formatCurrency(calculationResult.selling_price)}
              </div>
              <div className="text-sm text-green-600">Preço de Venda Final</div>
            </div>
          </div>
          {calculationResult.variations_applied.length > 0 && (
            <div className="mt-4">
              <h5 className="font-medium text-green-700 mb-2">Variações Aplicadas:</h5>
              <div className="flex flex-wrap gap-2">
                {calculationResult.variations_applied.map((variation, index) => (
                  <span key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                    {VARIATION_TYPES[variation.variation_type]}: {variation.variation_value}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lista de Variações */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">
            🎨 Variações de Preço ({variations.length})
          </h3>
          <button
            onClick={handleNewVariation}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            ➕ Nova Variação
          </button>
        </div>

        {/* Formulário de Variação */}
        {showForm && (
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h4 className="text-md font-semibold mb-4">
              {editingVariation ? '✏️ Editar Variação' : '➕ Nova Variação'}
            </h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Variação *
                  </label>
                  <select
                    name="variation_type"
                    value={formData.variation_type}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(VARIATION_TYPES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor *
                  </label>
                  <input
                    type="text"
                    name="variation_value"
                    value={formData.variation_value}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Branco, 15mm, 275x185"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unidade
                  </label>
                  <input
                    type="text"
                    name="variation_unit"
                    value={formData.variation_unit}
                    onChange={handleInputChange}
                    placeholder="Ex: mm, cm, kg"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Modificador de Preço (R$)
                  </label>
                  <input
                    type="number"
                    name="price_modifier"
                    value={formData.price_modifier}
                    onChange={handleInputChange}
                    step="0.01"
                    placeholder="0.00"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Multiplicador de Preço
                  </label>
                  <input
                    type="number"
                    name="price_multiplier"
                    value={formData.price_multiplier}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0.01"
                    placeholder="1.00"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingVariation(null);
                    resetForm();
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingVariation ? 'Atualizar' : 'Criar'} Variação
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Modificador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Multiplicador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {variations.map((variation) => (
                <tr key={variation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {VARIATION_TYPES[variation.variation_type]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {variation.variation_value}
                    {variation.variation_unit && (
                      <span className="text-gray-500 ml-1">({variation.variation_unit})</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(variation.price_modifier)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {variation.price_multiplier}x
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(variation)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => handleDelete(variation.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      🗑️ Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {variations.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhuma variação cadastrada para este custo.
          </div>
        )}
      </div>

      <ConfirmModal
        show={showConfirmModal}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmModal(false);
          setPendingDeleteId(null);
        }}
        title="Remover Variação"
        message="Tem certeza que deseja remover esta variação?"
        confirmText="Remover"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default CostVariationsManager;
